import {
  ChangeDetectionStrategy,
  Component,
  inject,
  signal,
} from '@angular/core';
import {
  email,
  form,
  FormField,
  required,
} from '@angular/forms/signals';
import {
  AppSvgIconComponent,
  SignalButton,
  SignalFormDirective,
  SignalTextField,
  State,
  validateSignalForm,
} from '@vallift/ngl';
import { BaseComponent } from '@core/base/base-component';
import { MyFormsAPIService } from '@core/api/my-forms/services/my-forms-api/myFormsAPI.service';
import { RespondentTokenStorageService } from '@core/services/respondent-token-storage.service';
import { environment } from '@environments/environment';
import type {
  MyFormsAuthApiResponse,
  StartMyFormsEmailOtpApiResponse,
} from '@core/api/my-forms/models';
import { safeMyFormsReturnUrl } from '../../utils/safe-return-url';

interface MyFormsAccessModel {
  email: string;
}

interface MyFormsOtpModel {
  otp: string;
}

@Component({
  selector: 'app-my-forms-access-page',
  imports: [
    AppSvgIconComponent,
    FormField,
    SignalButton,
    SignalFormDirective,
    SignalTextField,
  ],
  templateUrl: './my-forms-access-page.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MyFormsAccessPage extends BaseComponent {
  private readonly myFormsApi = inject(MyFormsAPIService);
  private readonly respondentTokenStorage = inject(RespondentTokenStorageService);

  readonly step = signal<'email' | 'otp'>('email');
  readonly challengeId = signal<string | null>(null);
  readonly devOtp = signal<string | null>(null);
  readonly startState = new State<StartMyFormsEmailOtpApiResponse>();
  readonly verifyState = new State<MyFormsAuthApiResponse>();

  readonly accessModel = signal<MyFormsAccessModel>({ email: '' });
  readonly otpModel = signal<MyFormsOtpModel>({ otp: '' });

  readonly accessForm = form(this.accessModel, (schemaPath) => {
    required(schemaPath.email, { message: 'Enter your email address' });
    email(schemaPath.email, { message: 'Enter a valid email address' });
  });

  readonly otpForm = form(this.otpModel, (schemaPath) => {
    required(schemaPath.otp, { message: 'Enter the verification code' });
  });

  constructor() {
    super();
    if (this.respondentTokenStorage.isAuthenticated()) {
      void this.router.navigateByUrl(this.postAuthDestination());
    }
  }

  onGoogleContinue(): void {
    const params = new URLSearchParams({
      returnUrl: this.postAuthDestination(),
    });
    window.location.href = `${environment.baseUrl}/api/my-forms/auth/google?${params}`;
  }

  onEmailContinue(): void {
    if (!validateSignalForm(this.accessForm)) {
      return;
    }

    this.executeRequest({
      state: this.startState,
      request: this.myFormsApi.startMyFormsEmailOtp({
        email: this.accessModel().email.trim(),
      }),
      showLoader: true,
      handleError: true,
      errorMessage: 'Could not send verification code',
      onSuccess: (response) => {
        this.challengeId.set(response.data.challengeId);
        this.devOtp.set(response.data.otp ?? null);
        this.otpModel.set({ otp: response.data.otp ?? '' });
        this.step.set('otp');
      },
    });
  }

  onVerifyOtp(): void {
    if (!validateSignalForm(this.otpForm)) {
      return;
    }
    const challengeId = this.challengeId();
    if (!challengeId) {
      this.toasterService.error('Start email verification again.');
      this.step.set('email');
      return;
    }

    this.executeRequest({
      state: this.verifyState,
      request: this.myFormsApi.verifyMyFormsEmailOtp({
        challengeId,
        otp: this.otpModel().otp.trim(),
      }),
      showLoader: true,
      handleError: true,
      errorMessage: 'Verification failed',
      onSuccess: (response) => {
        this.respondentTokenStorage.setSession(response.data);
        void this.router.navigateByUrl(this.postAuthDestination());
      },
    });
  }

  backToEmail(): void {
    this.step.set('email');
    this.challengeId.set(null);
    this.devOtp.set(null);
    this.otpModel.set({ otp: '' });
  }

  private postAuthDestination(): string {
    return (
      safeMyFormsReturnUrl(
        this.route.snapshot.queryParamMap.get('returnUrl'),
      ) ?? '/my-forms/inbox'
    );
  }
}
